import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Radio, Typography } from 'antd';

import { fetchItemHistory } from '../../services/history';
import { getUserId } from '../../utils';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatRelativeToNow } from '../../utils/formatters';
import { normalizeId } from '../../utils/ids';
import { conflictAuthors } from './staleConflict';

const { Paragraph } = Typography;

// Enough of the item's recent history to find the newest write of each
// conflicting field; a field changed more than this many entries ago in the
// window is said as "Someone".
const HISTORY_LIMIT = 50;

/**
 * "Someone changed this" — the one answer both item write paths give a 409
 * `stale_item` that touched a value the person ALSO changed
 * (staleConflict.sortStaleFields): the item form over every such field, the
 * card table's cell edit over the one it edited.
 *
 * A grid (live pass, 2026-09-26: labels and values read together when they
 * were one line of text): one ROW per conflicting field, its label first,
 * then a column for **Yours** and a column for **theirs**, headed with who
 * changed it and when ("Ada Lovelace · 2 minutes ago"). Each value is a
 * radio — click it, or Tab to the row and use the arrows — so the choice is
 * made on the value itself, and a chosen value shows a filled radio and a
 * heavier border, never colour alone. Every row starts on `defaultChoice`
 * (mine: the typing is what Save was pressed for). Cancel sends nothing;
 * **Save choices** hands the answers back. On a phone the columns stack
 * inside each row, each value carrying its own column name.
 *
 * Who and when come from the item's change log, read once when the dialog
 * opens (`GET /api/items/{id}/history?type=item&since=<the version read>`,
 * the item-write gate the person already passed; conflictAuthors). Until it
 * answers — or if it cannot — the column says "Their version", and a field
 * the log does not explain says "Someone".
 *
 *   conflicts  [{ name, label, mine, theirs, path? }] — `mine` / `theirs`
 *              already as text (conflictValueText); `path` is the change-log
 *              path when it is not `elements.<name>` (Notes: `notes`).
 *   item       the item AS IT STANDS (the 409's copy): its id and updatedAt.
 *   since      the `updatedAt` this browser read — where "their" changes start.
 *   title      "Someone changed this item" (form) / "… this field" (cell).
 *   context    'form' | 'cell' — only the lead sentence differs.
 *   onSave     (choices: { [name]: 'mine' | 'theirs' }).
 *   onCancel   nothing sent.
 */
export default function ItemConflictModal({
	open,
	conflicts = [],
	item = null,
	since = null,
	title = 'Someone changed this item',
	context = 'form',
	defaultChoice = 'mine',
	onSave,
	onCancel,
}) {
	// Only the rows the person has clicked; every other row is on
	// `defaultChoice` (resolved below), so the answer never waits on an effect.
	const [choices, setChoices] = useState({});
	const [authors, setAuthors] = useState(null); // null while asking
	const itemId = normalizeId(item?._id);

	useEffect(() => { setChoices({}); }, [open, conflicts]);
	const answers = useMemo(
		() => Object.fromEntries(conflicts.map(entry => [entry.name, choices[entry.name] || defaultChoice])),
		[conflicts, choices, defaultChoice]
	);

	useEffect(() => {
		if (!open || !itemId) return undefined;
		let live = true;
		setAuthors(null);
		fetchItemHistory(itemId, { type: 'item', ...(since ? { since } : {}), limit: HISTORY_LIMIT })
			.then(({ rows }) => { if (live) setAuthors(conflictAuthors(rows, conflicts, { me: getUserId() })); })
			.catch(() => { if (live) setAuthors({}); });
		return () => { live = false; };
	}, [open, itemId, since, conflicts]);

	// Who, per row, as the grid says it: the change log's answer, or
	// "Someone" when it had none (and nothing while it is still asked).
	const byRow = useMemo(() => Object.fromEntries(conflicts.map(entry => {
		const found = authors?.[entry.name] || null;
		const at = found?.at || item?.updatedAt || null;
		return [entry.name, authors ? { who: found?.who || 'Someone', at, isMe: Boolean(found?.isMe) } : null];
	})), [authors, conflicts, item]);
	const people = useMemo(() => [...new Set(Object.values(byRow).filter(Boolean).map(entry => entry.who))], [byRow]);
	const latest = useMemo(() => Object.values(byRow).filter(Boolean).map(entry => entry.at).filter(Boolean).sort().pop() || null, [byRow]);
	const oneAuthor = people.length === 1;

	const whenText = at => formatRelativeToNow(at);
	const whoWhen = entry => (entry ? [entry.who, whenText(entry.at)].filter(Boolean).join(' · ') : 'Their version');

	// The lead names the one who changed it; several (a colleague and an
	// automation, say) are named on their own values instead.
	const byYou = oneAuthor && Object.values(byRow).some(entry => entry?.isMe);
	const fields = conflicts.length === 1 ? 'This field was' : 'These fields were';
	let lead;
	if (context === 'cell') {
		lead = byYou
			? 'You changed this field somewhere else — another tab or window — after this list loaded. Choose the value to keep.'
			: `${authors && oneAuthor ? people[0] : 'Somebody'} changed this field after this list loaded. The row shows their value until you choose.`;
	} else if (byYou) {
		lead = `You saved this item somewhere else — another tab or window — while you were editing it here. Changes there to fields you did not touch are in the form now. ${fields} changed in both places — choose the value to keep in each row.`;
	} else if (authors && !oneAuthor) {
		lead = `This item was saved somewhere else while you were editing it. Changes to fields you did not touch are in the form now. ${fields} changed in both places — choose the value to keep in each row.`;
	} else {
		lead = `${authors ? people[0] : 'Somebody'} saved this item while you were editing it. Their changes to fields you did not touch are in the form now. ${fields} changed by both of you — choose the value to keep in each row.`;
	}

	const theirsHead = !authors
		? <span className="item-conflict__head-name">Their version</span>
		: oneAuthor
			? (
				<>
					<span className="item-conflict__head-name">{people[0]}</span>
					{latest ? <> · <time dateTime={latest} title={formatDisplayDateTime(latest)}>{whenText(latest)}</time></> : null}
				</>
			)
			: <span className="item-conflict__head-name">Theirs</span>;

	return (
		<Modal
			open={open}
			title={title}
			onCancel={onCancel}
			destroyOnHidden
			width={760}
			className="item-conflict-modal"
			footer={[
				<Button key="cancel" onClick={onCancel}>Cancel</Button>,
				<Button key="save" type="primary" onClick={() => onSave?.(answers)}>Save choices</Button>,
			]}
		>
			<Paragraph className="item-conflict__lead">{lead}</Paragraph>
			<div className="item-conflict" role="group" aria-label="Choose the value to keep for each field">
				<div className="item-conflict__head" aria-hidden="true">
					<div className="micro-label item-conflict__head-cell">Field</div>
					<div className="item-conflict__head-cell"><span className="item-conflict__head-name">Yours</span></div>
					<div className="item-conflict__head-cell">{theirsHead}</div>
				</div>
				{conflicts.map(entry => {
					const labelId = `item-conflict-${entry.name}`;
					const author = byRow[entry.name];
					const value = answers[entry.name];
					return (
						<div key={entry.name} className="item-conflict__row" role="radiogroup" aria-labelledby={labelId} data-conflict={entry.name}>
							<div className="item-conflict__field" id={labelId}>{entry.label}</div>
							<Radio.Group
								className="item-conflict__choices"
								name={labelId}
								value={value}
								onChange={event => setChoices(prev => ({ ...prev, [entry.name]: event.target.value }))}
							>
								<Radio value="mine" className="item-conflict__choice" data-choice="mine">
									<span className="item-conflict__col-name">Yours</span>
									<span className="item-conflict__value">{entry.mine}</span>
								</Radio>
								<Radio value="theirs" className="item-conflict__choice" data-choice="theirs">
									{/* The column name, per value: on a phone it is the only
									    header; with several authors it is this row's. */}
									<span className={`item-conflict__col-name${authors && !oneAuthor ? ' item-conflict__col-name--shown' : ''}`}>
										{whoWhen(author)}
									</span>
									<span className="item-conflict__value">{entry.theirs}</span>
								</Radio>
							</Radio.Group>
						</div>
					);
				})}
			</div>
		</Modal>
	);
}
